import React from 'react';
import ReactDOM from 'react-dom/client';
import { AppRouter } from './routes';
import './global.css';
import 'maplibre-gl/dist/maplibre-gl.css';

// Import dayjs Polish locale
import 'dayjs/locale/pl';

// Import i18n configuration
import './config/i18n';
import { I18nProvider } from './contexts/I18nContext';

// Import Mantine styles
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import {
  MantineProvider,
  createTheme,
  Modal,
  Drawer,
  v8CssVariablesResolver,
  type CSSVariablesResolver,
} from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { LocalizedDatesProvider } from './components/providers/LocalizedDatesProvider';
import tooltipClasses from './styles/Tooltip.module.css';
import { initializePWA } from './utils/pwaRegistration';
import {
  Z_INDEX_TOOLTIP_DEFAULT,
  Z_INDEX_NOTIFICATION,
  Z_INDEX_PANEL_DROPDOWN,
} from './constants/zIndex';
import {
  MAX_VISIBLE_NOTIFICATIONS,
  NOTIFICATION_AUTO_CLOSE_DEFAULT,
  INPUT_LABEL_FONT_SIZE,
  INPUT_LABEL_FONT_WEIGHT,
  INPUT_LABEL_MARGIN_BOTTOM,
} from './constants';

// Get the root element with proper type checking
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

const inputLabelStyles = {
  label: {
    fontSize: INPUT_LABEL_FONT_SIZE,
    fontWeight: INPUT_LABEL_FONT_WEIGHT,
    marginBottom: INPUT_LABEL_MARGIN_BOTTOM,
  },
} as const;

// Lift every Select/date dropdown above the fullscreen SlidePanel stacking
// context (`--z-fullscreen` 1260) and the mobile modal (8000) globally, so
// dropdowns/pickers stay visible in fullscreen forms without per-call-site
// props. Call sites that pass their own comboboxProps/popoverProps override this.
const DROPDOWN_Z_INDEX_PROPS = { zIndex: Z_INDEX_PANEL_DROPDOWN } as const;

// Create custom Mantine theme with app-specific overrides
const theme = createTheme({
  fontFamily:
    'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace:
    'Roboto Mono, "SF Mono", Monaco, Inconsolata, Consolas, monospace',

  colors: {
    // Custom semantic colors for task priorities and status
    priority: [
      '#10b981', // 0 - priority-low
      '#34d399', // 1
      '#f59e0b', // 2 - priority-medium
      '#fbbf24', // 3
      '#ef4444', // 4 - priority-high
      '#f87171', // 5
      '#8b5cf6', // 6 - priority-urgent
      '#a78bfa', // 7
      '#c4b5fd', // 8
      '#e0e7ff', // 9
    ],
    // Custom orange palette matching KeyCloak theme
    orange: [
      '#fff7ed', // 0 - lightest
      '#ffedd5', // 1
      '#fed7aa', // 2
      '#fdba74', // 3
      '#fb923c', // 4
      '#f97316', // 5
      '#f59e0b', // 6 - PRIMARY (KeyCloak button default)
      '#d97706', // 7
      '#b45309', // 8
      '#92400e', // 9 - darkest
    ],
  },

  primaryColor: 'indigo',
  defaultRadius: 'md',

  // Extended spacing scale (fills gaps below xs and between points)
  spacing: {
    '3xs': '0.375rem', // 6px
    '2xs': '0.25rem', // 4px
    'xs-sm': '0.5rem', // 8px
  },

  // Extended font size scale
  fontSizes: {
    '3xs': '0.625rem', // 10px
    '2xs': '0.6875rem', // 11px
    'xs-sm': '0.8125rem', // 13px
    'sm-md': '0.9375rem', // 15px
    '2xl': '1.375rem', // 22px
    '4xl': '2.125rem', // 34px
  },

  // Extended radius scale
  radius: {
    'sm-md': '0.375rem', // 6px
    'md-lg': '0.75rem', // 12px
    'lg-xl': '1.25rem', // 20px
  },

  other: {
    transitionFast: '150ms',
    transitionDefault: '200ms',
    transitionMedium: '300ms',
    transitionSlow: '500ms',
    easeDefault: 'ease',
    easeGlass: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeBounce: 'cubic-bezier(0.16, 1, 0.3, 1)',
    fontWeightMedium: '500',
    fontWeightSemibold: '600',
    fontWeightExtrabold: '800',
  },

  components: {
    Container: {
      defaultProps: {
        sizes: {
          xs: 540,
          sm: 720,
          md: 960,
          lg: 1140,
          xl: 1320,
        },
      },
    },
    Tooltip: {
      defaultProps: {
        withArrow: true,
        openDelay: 500,
        zIndex: Z_INDEX_TOOLTIP_DEFAULT,
      },
      classNames: {
        tooltip: tooltipClasses.tooltip,
      },
    },
    Checkbox: {
      defaultProps: {
        size: 'sm',
        radius: 'sm',
      },
      styles: () => ({
        root: {
          touchAction: 'manipulation',
          alignSelf: 'flex-start',
          flexShrink: 0,
          height: 'auto',
          maxHeight: 'var(--checkbox-size, 20px)',
        },
        inner: {
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        input: {
          touchAction: 'manipulation',
          cursor: 'pointer',
          flexShrink: 0,
          width: '100%',
          height: '100%',
        },
        icon: {
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        label: {
          cursor: 'pointer',
          userSelect: 'none',
        },
      }),
    },
    Drawer: Drawer.extend({
      defaultProps: {
        overlayProps: {
          backgroundOpacity: 0.7,
        },
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        radius: 'lg',
        overlayProps: {
          backgroundOpacity: 0.7, // MODAL_BACKDROP_OPACITY
        },
      },
    }),
    Alert: {
      defaultProps: {
        radius: 'lg',
      },
      styles: () => ({
        icon: {
          color: 'var(--mantine-color-text)',
          opacity: 0.9,
        },
      }),
    },
    Notification: {
      defaultProps: {
        radius: 'lg',
      },
      styles: () => ({
        root: {
          backgroundColor:
            'light-dark(var(--semi-transparent-bg-light), var(--semi-transparent-bg-dark))',
          boxShadow: 'var(--semi-transparent-shadow)',
        },
        icon: {
          color: 'var(--mantine-color-text)',
          opacity: 0.9,
        },
      }),
    },
    Button: {
      defaultProps: {
        radius: 'md',
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: 'md',
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        ...inputLabelStyles,
        innerInput: {
          height: 'var(--input-height)',
          lineHeight: 'var(--input-line-height)',
        },
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'md',
      },
      styles: inputLabelStyles,
    },
    Textarea: {
      defaultProps: {
        radius: 'md',
      },
      styles: inputLabelStyles,
    },
    Autocomplete: {
      defaultProps: {
        radius: 'md',
        comboboxProps: DROPDOWN_Z_INDEX_PROPS,
      },
      styles: inputLabelStyles,
    },
    Select: {
      defaultProps: {
        radius: 'md',
        comboboxProps: DROPDOWN_Z_INDEX_PROPS,
      },
      styles: inputLabelStyles,
    },
    MultiSelect: {
      defaultProps: {
        radius: 'md',
        comboboxProps: DROPDOWN_Z_INDEX_PROPS,
      },
      styles: inputLabelStyles,
    },
    ColorInput: {
      defaultProps: {
        radius: 'md',
      },
      styles: inputLabelStyles,
    },
    DateInput: {
      defaultProps: {
        radius: 'md',
        popoverProps: DROPDOWN_Z_INDEX_PROPS,
      },
      styles: inputLabelStyles,
    },
    DatePickerInput: {
      defaultProps: {
        radius: 'md',
        popoverProps: DROPDOWN_Z_INDEX_PROPS,
      },
      styles: inputLabelStyles,
    },
    DateTimePicker: {
      defaultProps: {
        radius: 'md',
        popoverProps: DROPDOWN_Z_INDEX_PROPS,
      },
      styles: inputLabelStyles,
    },
    InputBase: {
      defaultProps: {
        radius: 'md',
      },
      styles: inputLabelStyles,
    },
    Pagination: {
      defaultProps: {
        radius: 'md',
      },
    },
    Popover: {
      defaultProps: {
        radius: 'lg',
      },
    },
    Menu: {
      defaultProps: {
        radius: 'lg',
      },
    },
    Combobox: {
      styles: {
        optionsDropdownCheckIcon: {
          color:
            'light-dark(var(--mantine-primary-color-6), var(--mantine-primary-color-4))',
          opacity: 1,
        },
      },
    },
  },
});

// CSS Variables Resolver for theme.other values (transitions, easing, font-weights).
// Composes Mantine's v8CssVariablesResolver so the `light` variant keeps the
// translucent 8.x backgrounds (--mantine-color-*-light) instead of the new
// solid 9.x colors — preserves the pre-migration look across the app.
const cssVariablesResolver: CSSVariablesResolver = (resolverTheme) => {
  const base = v8CssVariablesResolver(resolverTheme);
  return {
    variables: {
      ...base.variables,
      '--mantine-transition-fast': resolverTheme.other.transitionFast,
      '--mantine-transition-default': resolverTheme.other.transitionDefault,
      '--mantine-transition-medium': resolverTheme.other.transitionMedium,
      '--mantine-transition-slow': resolverTheme.other.transitionSlow,
      '--mantine-ease-default': resolverTheme.other.easeDefault,
      '--mantine-ease-glass': resolverTheme.other.easeGlass,
      '--mantine-ease-bounce': resolverTheme.other.easeBounce,
      '--mantine-font-weight-medium': resolverTheme.other.fontWeightMedium,
      '--mantine-font-weight-semibold': resolverTheme.other.fontWeightSemibold,
      '--mantine-font-weight-extrabold':
        resolverTheme.other.fontWeightExtrabold,
    },
    light: {
      ...base.light,
      '--app-primary-glow-subtle': 'rgba(79, 70, 229, 0.06)',
      '--app-primary-glow-light': 'rgba(79, 70, 229, 0.1)',
      '--app-primary-glow': 'rgba(79, 70, 229, 0.2)',
    },
    dark: {
      ...base.dark,
      '--app-primary-glow-subtle': 'rgba(245, 158, 11, 0.08)',
      '--app-primary-glow-light': 'rgba(245, 158, 11, 0.12)',
      '--app-primary-glow': 'rgba(245, 158, 11, 0.2)',
    },
  };
};

// Initialize PWA with auto-update functionality
initializePWA({
  onRegisterError: (error) => {
    console.error('GeoMarkup PWA registration failed:', error);
  },
});

// Add ColorSchemeScript to document head
const colorSchemeScript = document.createElement('script');
colorSchemeScript.innerHTML = `
  try {
    const colorScheme = localStorage.getItem('mantine-color-scheme-value') || 'dark';
    document.documentElement.setAttribute('data-mantine-color-scheme', colorScheme);
  } catch (e) {
    document.documentElement.setAttribute('data-mantine-color-scheme', 'dark');
  }
`;
document.head.appendChild(colorSchemeScript);

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <I18nProvider>
      <MantineProvider
        theme={theme}
        defaultColorScheme="dark"
        cssVariablesResolver={cssVariablesResolver}
      >
        <LocalizedDatesProvider>
          <Notifications
            position="top-right"
            limit={MAX_VISIBLE_NOTIFICATIONS}
            autoClose={NOTIFICATION_AUTO_CLOSE_DEFAULT}
            zIndex={Z_INDEX_NOTIFICATION}
          />
          <AppRouter />
        </LocalizedDatesProvider>
      </MantineProvider>
    </I18nProvider>
  </React.StrictMode>
);
